<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * LaisMetricStrip — responsive row of small metric tiles for the top of a
 * dashboard / cockpit. It IS the top row of tiles, so never wrap it in a card
 * or nest another card inside a tile.
 *
 * Data-driven and self-contained: the domain passes normalized `items`; this
 * component owns only layout, tone tinting and loading skeletons. Unknown /
 * unrecognized tones fall back to `neutral` (never rendered as success).
 */
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  loading: { type: Boolean, default: false },
  // 0 = auto (derive a sensible responsive layout from the item count).
  columns: {
    type: Number,
    default: 0,
    validator: value => Number.isInteger(value) && value >= 0 && value <= 5,
  },
  ariaLabel: { type: String, default: '' },
  emptyLabel: { type: String, default: '' },
});

const { t } = useI18n();

// Canonical tone aliases (mirrors LaisStatusBadge) so an unknown backend tone
// is always neutralized instead of leaking as success.
const TONE_ALIASES = Object.freeze({
  neutral: 'neutral',
  unknown: 'neutral',
  muted: 'neutral',
  default: 'neutral',
  info: 'info',
  active: 'info',
  running: 'info',
  processing: 'info',
  positive: 'positive',
  success: 'positive',
  ok: 'positive',
  healthy: 'positive',
  warning: 'warning',
  pending: 'warning',
  stale: 'warning',
  danger: 'danger',
  error: 'danger',
  failed: 'danger',
  breach: 'danger',
});

// tone -> text color for the value + icon (canonical LAIS mapping).
const TONE_TEXT = Object.freeze({
  neutral: 'text-lais-text',
  info: 'text-lais-primary',
  positive: 'text-lais-mint-dark',
  warning: 'text-lais-text',
  danger: 'text-lais-danger',
});

const GRID_COLUMNS = Object.freeze({
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
});

const resolveTone = tone =>
  TONE_ALIASES[String(tone || '').toLowerCase()] || 'neutral';

const tiles = computed(() =>
  (props.items || []).map((item, index) => {
    const tone = resolveTone(item?.tone);
    const rawTrend = Number(item?.trend);
    const hasTrend = item?.trend !== null && Number.isFinite(rawTrend);
    const rawValue = item?.value;
    const hasValue =
      rawValue !== null && rawValue !== undefined && rawValue !== '';

    return {
      key: item?.id ?? index,
      label: item?.label == null ? '' : String(item.label),
      value: hasValue ? String(rawValue) : '—',
      helper: item?.helper == null ? '' : String(item.helper),
      icon: item?.icon || '',
      toneClass: TONE_TEXT[tone],
      trendUp: hasTrend && rawTrend > 0,
      trendDown: hasTrend && rawTrend < 0,
      trendDisplay: hasTrend ? `${rawTrend > 0 ? '+' : ''}${rawTrend}` : '',
    };
  })
);

// While loading, keep the strip's height stable by rendering the same number
// of skeleton tiles the real content will occupy.
const skeletonCount = computed(() => {
  if (props.columns >= 1) return props.columns;
  if (props.items?.length) return props.items.length;
  return 4;
});

const effectiveColumns = computed(() => {
  if (props.columns >= 1) return props.columns;
  const count = props.loading ? skeletonCount.value : tiles.value.length;
  return Math.min(Math.max(count, 1), 4);
});

const gridClass = computed(() => GRID_COLUMNS[effectiveColumns.value]);

const isEmpty = computed(() => !props.loading && tiles.value.length === 0);

const resolvedAriaLabel = computed(
  () => props.ariaLabel || t('LAIS_METRIC_STRIP.ARIA_LABEL')
);

const resolvedEmptyLabel = computed(
  () => props.emptyLabel || t('LAIS_METRIC_STRIP.EMPTY')
);
</script>

<template>
  <ul
    role="list"
    :aria-label="resolvedAriaLabel"
    :aria-busy="loading ? 'true' : undefined"
    class="grid list-none gap-3 md:gap-4"
    :class="gridClass"
  >
    <template v-if="loading">
      <li
        v-for="index in skeletonCount"
        :key="`skeleton-${index}`"
        class="flex min-h-[104px] flex-col justify-between gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card"
        aria-hidden="true"
      >
        <div class="h-3 w-1/2 animate-pulse rounded-full bg-lais-bg-soft" />
        <div class="h-7 w-3/4 animate-pulse rounded-lais bg-lais-bg-soft" />
        <div class="h-3 w-2/3 animate-pulse rounded-full bg-lais-bg-soft" />
      </li>
    </template>

    <li
      v-else-if="isEmpty"
      class="col-span-full flex min-h-[104px] items-center justify-center rounded-lais border border-dashed border-lais-border-soft bg-lais-bg-card p-4 text-center text-lais-body-sm text-lais-text-empty"
    >
      {{ resolvedEmptyLabel }}
    </li>

    <template v-else>
      <li
        v-for="tile in tiles"
        :key="tile.key"
        class="flex min-h-[104px] flex-col justify-between gap-2 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card"
      >
        <div class="flex items-start justify-between gap-2">
          <p
            v-if="tile.label"
            class="min-w-0 truncate font-redHatText text-lais-label-sm font-semibold uppercase tracking-[0.04em] text-lais-text-helper"
          >
            {{ tile.label }}
          </p>
          <span
            v-if="tile.icon"
            class="size-4 shrink-0"
            :class="[tile.icon, tile.toneClass]"
            aria-hidden="true"
          />
        </div>

        <p
          class="font-redHatDisplay text-lais-heading-lg font-semibold leading-none tabular-nums"
          :class="tile.toneClass"
        >
          {{ tile.value }}
        </p>

        <div class="flex items-center justify-between gap-2">
          <p
            v-if="tile.helper"
            class="min-w-0 truncate font-redHatText text-lais-body-xs text-lais-text-helper"
          >
            {{ tile.helper }}
          </p>
          <span
            v-if="tile.trendUp || tile.trendDown"
            class="inline-flex shrink-0 items-center gap-1 text-lais-label-sm font-semibold tabular-nums"
            :class="tile.trendUp ? 'text-lais-mint-dark' : 'text-lais-danger'"
          >
            <span
              class="size-3.5 shrink-0"
              :class="
                tile.trendUp ? 'i-lucide-trending-up' : 'i-lucide-trending-down'
              "
              aria-hidden="true"
            />
            {{ tile.trendDisplay }}
          </span>
        </div>
      </li>
    </template>
  </ul>
</template>
